import { useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../api/client';
import { manufacturingBoardKey, manufacturingBoardWrite, settleWrite } from '../api/write-keys';
import { useSession } from '../auth/session';
import { Alert, Button, Chip, Empty, Field, Modal, Note, PageHeader, Panel, Spinner, Tiles } from '../ui';
import { SearchBox } from '../ui/SearchBox';
import { useDebounced } from '../ui/use-debounced';
import { currentOrder, shortfallLines } from './manufacturing/board-model';
import { ProductionTable } from './manufacturing/ProductionTable';

/**
 * A-26 Manufacturing board — MF-01…MF-06 and DB-10. Manufacturing lands here (Screen Flow §3).
 *
 * What it is for: which confirmed orders are waiting, which are in manufacture, which are late
 * against the date the retailer was given, and recording each piece as it is made — with its
 * Job Number, at that moment (D77), as a piece row in MADE.
 *
 * Production state IS the order's state (MF-04, fixed in v1): moving an order into manufacture,
 * or closing it as made, goes through the order's own transition route and its own permission.
 * Closing it is what makes a shortfall final — the board says so before it lets you.
 *
 * Every tile is derived and is a filter on the board below it (DB-12): a figure with no rows
 * behind it is not a tile.
 */

type State = 'APPROVED' | 'AWAITING_ADVANCE' | 'IN_MANUFACTURING' | 'READY';
interface Line {
  id: string; lineNumber: number; variantCode: string; description: string | null; size: string | null;
  ordered: number; made: number; outstanding: number; shortfall: number | null;
}
/** MF-07 — who accepted the work, and when. Null until the workshop acknowledges it. */
interface Acknowledgement { at: string; by: string | null }
interface Order {
  id: string; reference: string; retailer: string; state: State; stateLabel: string;
  /** MF-07 — a fact about the order, not a state of it: an acknowledged order is still APPROVED. */
  acknowledged: Acknowledgement | null;
  confirmedOn: string | null; dispatchBy: string | null; dispatchSource: 'promised' | 'estimated' | null;
  pastPromised: boolean; lines: Line[];
}
interface Board {
  today: string;
  leadTime: { minDays: number; maxDays: number; source: 'portal' | 'default' };
  tiles: {
    byState: Array<{ state: State; label: string; orders: number }>;
    made: number; ordered: number; pastPromised: number; madeNotShipped: number; unacknowledged: number;
  };
  orders: Order[];
}
type Filter = { kind: 'all' } | { kind: 'state'; state: State } | { kind: 'late' } | { kind: 'unacknowledged' };

const TONE: Record<State, 'us' | 'run' | 'done' | 'lapse'> = {
  APPROVED: 'us', AWAITING_ADVANCE: 'lapse', IN_MANUFACTURING: 'run', READY: 'done',
};

export function ManufacturingBoard() {
  const { portal, can } = useSession();
  const qc = useQueryClient();
  // `draft` is what is being typed; `term` is what is searched. Typing searches by itself once it
  // pauses (~400 ms); Enter searches at once. The server narrows the list on `term`, so the browser is
  // never sent every order to filter for itself.
  const [draft, setDraft] = useState('');
  const [term, setTerm] = useState('');
  const settled = useDebounced(draft.trim(), 400);
  useEffect(() => { setTerm(settled); }, [settled]);
  const key = manufacturingBoardKey(portal?.code ?? null);
  const { data: fetched, isLoading, isFetching, error } = useQuery({
    // One cache entry per term, so a slow earlier search can never overwrite a later one's result.
    queryKey: [...key, term],
    // `signal` aborts the request when the term changes, so a superseded search is cancelled, not just ignored.
    queryFn: ({ signal }) => api.get<Board>(`/manufacturing/board${term ? `?order=${encodeURIComponent(term)}` : ''}`, { signal }),
    // The previous board stays on screen while a search runs, so the page does not flash.
    placeholderData: keepPreviousData,
  });
  // A failed search keeps the last board visible, with the reason above it, rather than replacing the page.
  const lastGood = useRef<Board | undefined>(undefined);
  if (fetched) lastGood.current = fetched;
  const board = fetched ?? lastGood.current;
  const [filter, setFilter] = useState<Filter>({ kind: 'all' });
  /*
   * A dialog holds the order's ID, never the order. It used to hold the object snapshotted when
   * the row was drawn, and so showed figures from before the last save — see `board-model.ts`.
   */
  const [revisingId, setRevisingId] = useState<string | null>(null);
  const [closingId, setClosingId] = useState<string | null>(null);
  /** MF-07 — Admin asked to start work the workshop has not accepted. Permitted, and said out loud. */
  const [startingUnackedId, setStartingUnackedId] = useState<string | null>(null);
  /** The action in flight, as `orderId:action`, so only the button that was pressed changes. */
  const [busy, setBusy] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  if (isLoading && !board) return <Spinner />;
  if (!board) return <Alert tone="stop">{error instanceof Error ? error.message : 'Could not load the board.'}</Alert>;

  const mayRecord = can('manufacturing.update');
  // MF-03 moves are the order's own transitions, so they carry the order's permission.
  const mayMove = can('order.approve');
  /*
   * Moving an order from here moves the ORDER, so the order queue and the consignment-invoice
   * candidates change with the board — one statement of that, shared with `useOrderTransition`,
   * so the two cannot drift (`api/write-keys.ts`).
   *
   * Of that set only the board is awaited. Awaiting all of it is what left "Acknowledge" on
   * "Acknowledging…" for ever: the order queue's prefix covers every cached order detail, and one
   * slow query under it held a button on a screen those queries do not even draw.
   */
  const refresh = () => settleWrite(qc, manufacturingBoardWrite(portal?.code ?? null));
  const act = (tag: string, fn: () => Promise<unknown>, done?: () => void) => {
    setFailure(null);
    setBusy(tag);
    void (async () => {
      try {
        await fn();
        // The re-read lands BEFORE the dialog closes and before the button comes back, so what
        // a person sees next is the board the server has — not the one they acted on.
        await refresh();
        done?.();
      } catch (e) {
        setFailure(e instanceof ApiError ? e.message : 'That did not work.');
      } finally {
        setBusy(null);
      }
    })();
  };
  const transition = (o: Order, action: 'beginManufacture' | 'markReady') =>
    act(`${o.id}:${action}`, () => api.post(`/orders/${o.id}/transition`, { action }), () => {
      setClosingId(null);
      setStartingUnackedId(null);
    });
  /**
   * MF-07 — the workshop accepts the work. NOT a status change: the order stays exactly where it
   * is, and what changes is that it now records who took it on.
   */
  const acknowledge = (o: Order) =>
    act(`${o.id}:acknowledge`, () => api.post(`/manufacturing/orders/${o.id}/acknowledge`, {}));

  const shown = board.orders.filter((o) => (filter.kind === 'all' ? true
    : filter.kind === 'late' ? o.pastPromised
      : filter.kind === 'unacknowledged' ? !o.acknowledged && o.state !== 'READY'
        : o.state === filter.state));
  // Resolved from the board on every render, so a dialog never shows a figure the board has
  // since replaced — and closes itself if its order has left the board entirely.
  const revising = currentOrder(board.orders, revisingId);
  const closing = currentOrder(board.orders, closingId);
  const startingUnacked = currentOrder(board.orders, startingUnackedId);
  const shortfall = closing ? shortfallLines(closing.lines) : [];

  return (
    <>
      <PageHeader
        title="Manufacturing"
        // The portal and ITS day, kept because every age and due date on this board is read
        // against that day and not the viewer's (D49) — a person in another zone reading
        // "2 days late" needs to know late by whose clock. The rest of this subtitle described
        // the screen to somebody already looking at it; MF-01 is a note for us, and lives in
        // the service that enforces it.
        sub={`${portal?.name ?? 'This portal'} · today is ${board.today} here`}
      />
      {failure ? <Alert tone="stop">{failure}</Alert> : null}

      <Tiles items={[
        ...board.tiles.byState.map((t) => ({
          k: t.label,
          v: <button type="button" className="mk-linkbtn" onClick={() => setFilter({ kind: 'state', state: t.state })}>{t.orders}</button>,
          s: 'orders',
        })),
        {
          // MF-07 — the workshop's own queue, and the first thing it should look at.
          k: 'Awaiting acknowledgement',
          v: <button type="button" className="mk-linkbtn" onClick={() => setFilter({ kind: 'unacknowledged' })}>{board.tiles.unacknowledged}</button>,
          s: 'orders nobody has accepted',
        },
        { k: 'Made against ordered', v: `${board.tiles.made} / ${board.tiles.ordered}`, s: 'pieces on the board' },
        {
          k: 'Past promised dispatch',
          v: <button type="button" className="mk-linkbtn" onClick={() => setFilter({ kind: 'late' })}>{board.tiles.pastPromised}</button>,
          s: 'orders',
        },
        { k: 'Made, not yet shipped', v: board.tiles.madeNotShipped, s: 'pieces with a Job Number (D77)' },
      ]} />

      {error ? <Alert tone="stop">{`The search could not be completed: ${error instanceof Error ? error.message : 'please try again.'}`}</Alert> : null}
      <div style={{ marginBottom: 'var(--mk-space-4)' }}>
        <SearchBox
          label="Find an order by number" placeholder="Search order number, e.g. MK-1033"
          value={draft} busy={isFetching && !isLoading}
          onChange={setDraft}
          onSubmit={() => setTerm(draft.trim())}
          onClear={() => { setDraft(''); setTerm(''); }}
        />
      </div>

      <Panel
        title={filter.kind === 'all' ? 'All orders in production'
          : filter.kind === 'late' ? 'Past promised dispatch'
            : filter.kind === 'unacknowledged' ? 'Awaiting acknowledgement'
              : board.tiles.byState.find((t) => t.state === filter.state)?.label ?? ''}
        actions={filter.kind !== 'all' ? <Button tone="ghost" size="sm" onClick={() => setFilter({ kind: 'all' })}>Show all</Button> : null}
        pad
      >
        {shown.length === 0 ? (
          <Empty
            title={term ? 'No matching order found' : 'Nothing here'}
            body={term ? `No order in production matches "${term}". Check the number, or clear the search.` : filter.kind === 'all' ? 'No confirmed order on this portal is waiting for or in production.'
              : filter.kind === 'late' ? 'No order on this portal is past its promised dispatch date.'
                : filter.kind === 'unacknowledged' ? 'Manufacturing has acknowledged every order on this portal.'
                  : 'No order on this portal is in this state.'}
          />
        ) : shown.map((o) => (
          <div key={o.id} style={{ marginBottom: 'var(--mk-space-5)' }}>
            <div style={{ display: 'flex', gap: 'var(--mk-space-3)', alignItems: 'center', flexWrap: 'wrap' }}>
              <b>{o.reference}</b>
              <span>{o.retailer}</span>
              <Chip tone={TONE[o.state]}>{o.stateLabel}</Chip>
              <span className="mk-hint">
                Confirmed {o.confirmedOn ?? '—'} · Dispatch by {o.dispatchBy ?? '—'}
                {o.dispatchSource === 'estimated' ? ' (estimated)' : ''}
              </span>
              {o.pastPromised ? <Chip tone="stop">Past promised dispatch</Chip> : null}
              {/* MF-07 — whether the workshop has accepted this work, on the row, beside the
                  buttons that act on it. A READY order is past the point where it would mean
                  anything, so it is not labelled either way. */}
              {o.state === 'READY' ? null : o.acknowledged ? (
                <Chip tone="done" title={`Acknowledged ${o.acknowledged.at.slice(0, 10)}${o.acknowledged.by ? ` by ${o.acknowledged.by}` : ''}`}>
                  {o.acknowledged.by ? `Acknowledged by ${o.acknowledged.by}` : 'Acknowledged'}
                </Chip>
              ) : <Chip tone="lapse">Not acknowledged</Chip>}
              <span className="mk-spacer" />
              {/* Each button says it is working and refuses a second press until the board has been
                  re-read, so a slow round trip cannot be mistaken for nothing having happened. */}
              {mayRecord && !o.acknowledged && o.state !== 'READY' ? (
                <Button size="sm" disabled={busy !== null} onClick={() => acknowledge(o)}>
                  {busy === `${o.id}:acknowledge` ? 'Acknowledging…' : 'Acknowledge'}
                </Button>
              ) : null}
              {mayMove && o.state === 'APPROVED' ? (
                <Button
                  size="sm" tone={o.acknowledged ? 'solid' : 'ghost'} disabled={busy !== null}
                  /*
                   * MF-07 — Admin MAY start work the workshop has not accepted (the reasoning is
                   * in the API's `manufacturing.board.ts`, at `MAY_START_UNACKNOWLEDGED`), and the
                   * screen says so rather than silently doing it: an unacknowledged order asks
                   * first. The button itself is quieter until the work has been accepted.
                   */
                  onClick={() => (o.acknowledged ? transition(o, 'beginManufacture') : setStartingUnackedId(o.id))}
                >
                  {busy === `${o.id}:beginManufacture` ? 'Starting…'
                    : o.acknowledged ? 'Start manufacture' : 'Start manufacture (unacknowledged)'}
                </Button>
              ) : null}
              {/* Closing production is Admin acting on what has been recorded here, so it is offered
                  once at least one piece is recorded as made. The API refuses it before then too. */}
              {mayMove && o.state === 'IN_MANUFACTURING'
                ? (o.lines.some((l) => l.made > 0)
                  ? <Button size="sm" tone="ghost" disabled={busy !== null} onClick={() => setClosingId(o.id)}>Mark ready</Button>
                  : <span className="mk-hint">Record the quantity made to mark ready</span>)
                : null}
              {mayRecord ? <Button size="sm" tone="ghost" disabled={busy !== null} onClick={() => setRevisingId(o.id)}>Revise dispatch date</Button> : null}
            </div>
            <ProductionTable
              orderId={o.id}
              reference={o.reference}
              state={o.state}
              lines={o.lines}
              mayEdit={mayRecord}
              onSaved={refresh}
            />
          </div>
        ))}
      </Panel>

      {revising ? <Revise order={revising} onDone={() => setRevisingId(null)} act={act} busy={busy !== null} /> : null}
      {/*
        MF-07 — starting production on work nobody has accepted. Allowed, and not quietly: a
        workshop that is slow to click must not be able to halt the business, but the person
        starting it should know that is what they are doing, and the audit row records it.
      */}
      {startingUnacked && !startingUnacked.acknowledged ? (
        <Modal
          title={`Start manufacture — ${startingUnacked.reference}`}
          onClose={() => setStartingUnackedId(null)}
          footer={<>
            <Button disabled={busy !== null} onClick={() => transition(startingUnacked, 'beginManufacture')}>
              {busy === `${startingUnacked.id}:beginManufacture` ? 'Starting…' : 'Start anyway'}
            </Button>
            <Button tone="ghost" disabled={busy !== null} onClick={() => setStartingUnackedId(null)}>Wait for Manufacturing</Button>
          </>}
        >
          <Alert tone="them">
            <b>Manufacturing has not acknowledged this order.</b> Nobody has yet recorded that the workshop has
            the work, and the retailer has already been given a dispatch date of{' '}
            {startingUnacked.dispatchBy ?? 'none'} (MF-02).
          </Alert>
          <Note>
            Starting it anyway is allowed and is recorded as started unacknowledged, with your name. If the
            workshop acknowledges it later, that is still recorded.
          </Note>
        </Modal>
      ) : null}
      {closing ? (
        <Modal
          title={`Mark ready — ${closing.reference}`}
          onClose={() => setClosingId(null)}
          footer={<>
            <Button
              disabled={busy !== null}
              onClick={() => transition(closing, 'markReady')}
            >
              {busy === `${closing.id}:markReady` ? 'Marking ready…' : 'Mark ready'}
            </Button>
            <Button tone="ghost" disabled={busy !== null} onClick={() => setClosingId(null)}>Not yet</Button>
          </>}
        >
          <Alert tone="them">
            This marks what has been made as <b>ready</b>, so it can be invoiced and shipped. Anything not yet made
            stays on the order as &ldquo;yet to be ready&rdquo;, and you can still record those pieces here afterwards.
            The retailer will see the order as <b>partially ready</b> until every piece is made.
          </Alert>
          {/* From the board as it stands, which is why `closing` is looked up rather than kept:
              a made line has no shortfall and must not be named here. */}
          {shortfall.length === 0 ? (
            <Note>Every piece on this order has been made.</Note>
          ) : shortfall.map((l) => (
            <Note key={l.id}>Line {l.lineNumber}, {l.description ?? l.variantCode}: {l.outstanding} of {l.ordered} not made yet.</Note>
          ))}
        </Modal>
      ) : null}
    </>
  );
}

type Act = (tag: string, fn: () => Promise<unknown>, done?: () => void) => void;

/** MF-05 — a delay: the revised dispatch date and the reason, both audited. */
function Revise({ order, onDone, act, busy }: { order: Order; onDone: () => void; act: Act; busy: boolean }) {
  const [date, setDate] = useState(order.dispatchBy ?? '');
  const [reason, setReason] = useState('');
  return (
    <Modal
      title={`Revise dispatch date — ${order.reference}`}
      onClose={onDone}
      footer={<>
        <Button
          disabled={busy || !date || reason.trim().length < 10}
          onClick={() => act(`${order.id}:dispatch-date`, () => api.post(`/manufacturing/orders/${order.id}/dispatch-date`, { revisedOn: date, reason }), onDone)}
        >
          {busy ? 'Saving…' : 'Save revised date'}
        </Button>
        <Button tone="ghost" disabled={busy} onClick={onDone}>Cancel</Button>
      </>}
    >
      <Field label="Revised dispatch date"><input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></Field>
      <Field label="Why" hint="Required. Recorded with your name, and what the retailer is told.">
        <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Modal>
  );
}
